import React, { useEffect, useRef, useState } from 'react';
import { MapPin, ChevronDown, Building2, Check } from 'lucide-react';
import axios from '../../axiosConfig';
import './LocationSwitcher.css';

/**
 * Topbar location switcher.
 *
 * Self-contained: fetches the merchant's locations, persists the active
 * selection to localStorage, and broadcasts a `locationChanged` window
 * event so other listeners (analytics widgets, etc.) can react. Renders
 * nothing when the merchant has 0–1 locations.
 *
 * Owners see an "All Locations" entry; team members are pinned to a
 * specific assigned location and never see the "all" option.
 */
const LOCATION_KEY = 'selectedLocationId';

const LocationSwitcher = () => {
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Fetch locations + initialize selection from localStorage
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/locations');
        if (!data?.success || cancelled) return;

        const fetched = data.locations || [];
        const team = !!data.isTeamMember;
        const primary = fetched.find((l) => l.isPrimary || l.assignmentIsPrimary);
        const savedId = localStorage.getItem(LOCATION_KEY);

        setLocations(fetched);
        setIsTeamMember(team);

        if (team) {
          // Team members never get "All Locations"; default to primary.
          const next =
            (savedId && savedId !== 'all' && fetched.find((l) => l.id === savedId)) ||
            primary ||
            fetched[0] ||
            null;
          setSelected(next);
          if (next) localStorage.setItem(LOCATION_KEY, next.id);
        } else if (savedId === 'all') {
          setSelected(null);
        } else if (savedId) {
          setSelected(fetched.find((l) => l.id === savedId) || primary || fetched[0] || null);
        } else {
          setSelected(primary || fetched[0] || null);
        }
      } catch (err) {
        console.error('[LocationSwitcher] Failed to load locations:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const choose = (next) => {
    setSelected(next);
    localStorage.setItem(LOCATION_KEY, next ? next.id : 'all');
    setOpen(false);
    window.dispatchEvent(new CustomEvent('locationChanged', { detail: next }));
  };

  // Hide entirely when there's nothing meaningful to switch between.
  if (locations.length <= 1) return null;

  return (
    <div className="loc-switcher" ref={wrapRef}>
      <button
        type="button"
        className="loc-switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <MapPin size={16} className="loc-switcher-pin" aria-hidden="true" />
        <span className="loc-switcher-label">{selected?.name || 'All Locations'}</span>
        <ChevronDown
          size={16}
          className={`loc-switcher-chevron ${open ? 'open' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="loc-switcher-menu" role="listbox">
          {!isTeamMember && (
            <button
              type="button"
              className={`loc-switcher-item ${!selected ? 'active' : ''}`}
              onClick={() => choose(null)}
              role="option"
              aria-selected={!selected}
            >
              <Building2 size={18} className="loc-switcher-item-icon all" aria-hidden="true" />
              <div className="loc-switcher-item-body">
                <div className="loc-switcher-item-title">All Locations</div>
                <div className="loc-switcher-item-sub">View combined data</div>
              </div>
              {!selected && <Check size={18} className="loc-switcher-item-check" aria-hidden="true" />}
            </button>
          )}

          {locations.map((loc) => (
            <button
              key={loc.id}
              type="button"
              className={`loc-switcher-item ${selected?.id === loc.id ? 'active' : ''}`}
              onClick={() => choose(loc)}
              role="option"
              aria-selected={selected?.id === loc.id}
            >
              <MapPin
                size={18}
                className={`loc-switcher-item-icon ${loc.isPrimary ? 'primary' : ''}`}
                aria-hidden="true"
              />
              <div className="loc-switcher-item-body">
                <div className="loc-switcher-item-title">{loc.name}</div>
                {loc.address && <div className="loc-switcher-item-sub">{loc.address}</div>}
              </div>
              {selected?.id === loc.id && (
                <Check size={18} className="loc-switcher-item-check" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationSwitcher;
